// pages/sections/clientBillingReportUtil.js
// Pure helpers + the cached fetch for ClientBillingReportModal (the read-only
// Client Billing Report, ported from swa-botfieldBilling's ClientBillingReport).
//
// Report rows are one per client with period-prefixed metric fields
// (YTD_WIPAmount, PYYTD_BillAmount, TTM_..., PYTTM_...). The server has already
// scoped them to the viewer (see api/diClientBillingReport) and flagged each row
// `inScope` (the viewer's own client) or not (a billing / relationship group
// counterpart pulled in only so group totals are complete).

import { useEffect, useState } from 'react';
import { getImpersonationEmail } from '../../auth/authFetch';

// ── Metric definitions ───────────────────────────────────────────────────────

// Metric columns. Raw fields read as `${prefix}_${key}` (e.g. TTM_BillAmount);
// Realization is computed (Bill / WIP).
export const METRICS = [
  { label: 'WIP Hours',   key: 'WIPHours',   kind: 'hours' },
  { label: 'WIP Amount',  key: 'WIPAmount',  kind: 'money' },
  { label: 'Bill Amount', key: 'BillAmount', kind: 'money' },
  { label: 'Realization', key: 'Realization', kind: 'ratio',
    compute: (m, p) => {
      const bill = mval(m, p, 'BillAmount');
      const wip = mval(m, p, 'WIPAmount');
      return wip ? bill / wip : null;
    } },
  { label: 'Disb Amount', key: 'DisbAmount', kind: 'money' },
  { label: 'Disb Billed', key: 'DisbBilled', kind: 'money' },
];
// Additive raw fields (Realization is derived, so it is not summed).
const SUM_KEYS = ['WIPHours', 'WIPAmount', 'BillAmount', 'DisbAmount', 'DisbBilled'];
// Every period prefix on a report row — summed for group roll-ups so both scopes
// work without re-aggregating on toggle.
const ALL_PREFIXES = ['YTD', 'PYYTD', 'TTM', 'PYTTM'];

// Period-scope toggle -> (current-year prefix, prior-year prefix).
export const SCOPES = {
  YTD: { label: 'YTD', cy: 'YTD', py: 'PYYTD' },
  TTM: { label: 'TTM', cy: 'TTM', py: 'PYTTM' },
};

export const GROUP_VIEWS = [
  { key: 'client',       label: 'Client' },
  { key: 'billing',      label: 'Billing Group' },
  { key: 'relationship', label: 'Relationship Group' },
];

// ── Value access / formatting ────────────────────────────────────────────────

// Coerce a possibly-formatted value (e.g. "1,234.50") to a finite number or null.
// The feed can return numeric fields as comma-formatted strings, which a bare
// Number() turns to NaN.
export function toNum(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string') v = v.replace(/,/g, '').trim();
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Case-insensitive field accessor, cached per object via a WeakMap — the feed's
// field casing isn't guaranteed consistent (the hours field can differ from the
// amount fields).
const _lcKeyCache = new WeakMap();
export function ciGet(obj, key) {
  if (!obj || typeof obj !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  let m = _lcKeyCache.get(obj);
  if (!m) {
    m = {};
    for (const k of Object.keys(obj)) m[k.toLowerCase()] = obj[k];
    _lcKeyCache.set(obj, m);
  }
  return m[key.toLowerCase()];
}

// Numeric metric getter — null for absent/blank so "no data" reads as a dash.
function mval(metrics, prefix, key) {
  return toNum(ciGet(metrics, `${prefix}_${key}`));
}

// Value of a metric for a period prefix — computed metrics (Realization) derive
// from other fields; the rest read straight off the row.
export function metricValue(metrics, prefix, metric) {
  return metric.compute ? metric.compute(metrics, prefix) : mval(metrics, prefix, metric.key);
}

function fmtCurrency(n) {
  if (n == null) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtHours(n) {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
// Format a metric value per its kind: money -> $, hours -> 0-2dp, ratio -> % (2dp).
export function fmtMetricVal(metric, v) {
  if (v == null || !Number.isFinite(v)) return '—';
  if (metric.kind === 'ratio') return (v * 100).toFixed(2) + '%';
  return metric.kind === 'hours' ? fmtHours(v) : fmtCurrency(v);
}

// Year-over-year change for the CY chip's corner badge. Money/hours -> percent
// change; ratios (Realization) -> percentage-point difference. null when there is
// no comparable prior-year value.
export function yoyDelta(metric, cyv, pyv) {
  if (cyv == null || pyv == null) return null;
  if (metric.kind === 'ratio') {
    const d = (cyv - pyv) * 100;
    return { text: `${d > 0 ? '+' : ''}${d.toFixed(1)}pp`, dir: d > 0 ? 'pos' : d < 0 ? 'neg' : '' };
  }
  if (pyv === 0) return null;
  const pct = ((cyv - pyv) / Math.abs(pyv)) * 100;
  return { text: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`, dir: pct > 0 ? 'pos' : pct < 0 ? 'neg' : '' };
}

// Reporting window for the selected scope. The report is a point-in-time snapshot
// as of end-of-previous-month; YTD runs from Jan 1 of that year, TTM is the
// trailing twelve months ending there.
export function reportDateRange(scopeKey) {
  const now = new Date();
  const asOf = new Date(now.getFullYear(), now.getMonth(), 0);   // last day of prev month
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  let start;
  if (scopeKey === 'TTM') {
    start = new Date(asOf);
    start.setFullYear(start.getFullYear() - 1);
    start.setDate(start.getDate() + 1);
  } else {
    start = new Date(asOf.getFullYear(), 0, 1);
  }
  return `${fmt(start)} – ${fmt(asOf)}`;
}

// ── Grouping / entities ──────────────────────────────────────────────────────

const codeOf = r => String(ciGet(r, 'ClientCode') ?? '').trim();
export const compareCodes = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true });

// Resolve a client to its group anchor for the chosen grouping. Billing-group
// edges are child->parent (walked, bounded, in case of multi-hop legacy data);
// relationship groups are member->anchor.
export function anchorFor(code, groupBy, groupings) {
  if (groupBy === 'billing') {
    const map = (groupings && groupings.billingGroups) || {};
    let cur = code, hops = 0;
    while (map[cur] && map[cur] !== cur && hops < 20) { cur = map[cur]; hops++; }
    return cur;
  }
  if (groupBy === 'relationship') {
    const map = (groupings && groupings.relationshipGroups) || {};
    return map[code] || code;
  }
  return code;
}

// Build display entities — one per client (flat) or one per group anchor with every
// metric summed over the group's FULL membership. `focusCodes` (a Set of ClientCodes,
// or null) narrows what is shown:
//   * flat Client view  — only those clients (null: every in-scope client);
//   * group views       — only groups that contain one of those clients (null: groups
//     containing any in-scope client), but each group sums ALL its members, including
//     ones outside the viewer's book (flagged inScope:false -> de-emphasised).
// Rows are never mutated (they belong to a shared cache).
export function buildEntities(rows, { groupBy, groupings, focusCodes }) {
  const list = Array.isArray(rows) ? rows : [];
  const wanted = r => (focusCodes ? focusCodes.has(codeOf(r)) : r.inScope !== false);
  const mk = (code, anchorRow, members, metrics, isGroup) => ({
    key: code, code,
    name: ciGet(anchorRow, 'ClientName') || code,
    office: ciGet(anchorRow, 'ClientOffice'),
    originator: ciGet(anchorRow, 'ClientOriginator'),
    partner: ciGet(anchorRow, 'ClientPartner'),
    manager: ciGet(anchorRow, 'ClientManager'),
    memberCount: members.length, members, metrics, isGroup,
  });

  if (groupBy === 'client') {
    return list.filter(wanted).map(c => mk(codeOf(c), c, [c], c, false));
  }

  const buckets = new Map();
  for (const c of list) {
    const a = anchorFor(codeOf(c), groupBy, groupings);
    if (!buckets.has(a)) buckets.set(a, []);
    buckets.get(a).push(c);
  }
  const out = [];
  for (const [anchor, members] of buckets) {
    if (!members.some(wanted)) continue;
    const metrics = {};
    for (const p of ALL_PREFIXES) for (const k of SUM_KEYS) {
      const f = `${p}_${k}`;
      metrics[f] = members.reduce((s, x) => s + (toNum(ciGet(x, f)) || 0), 0);
    }
    const anchorRow = members.find(x => codeOf(x) === anchor) || members[0];
    out.push(mk(anchor, anchorRow, members, metrics, true));
  }
  return out;
}

export function sortEntities(entities, sort, scope) {
  const mul = sort.dir === 'asc' ? 1 : -1;
  const arr = [...entities];
  arr.sort((a, b) => {
    if (sort.key === 'name')   return mul * compareCodes(a.name, b.name);
    if (sort.key === 'office') return mul * compareCodes(a.office, b.office);
    if (sort.key.startsWith('metric:')) {
      const metric = METRICS.find(m => m.key === sort.key.slice(7));
      const av = metric ? metricValue(a.metrics, scope.cy, metric) : null;
      const bv = metric ? metricValue(b.metrics, scope.cy, metric) : null;
      if (av == null && bv == null) return compareCodes(a.code, b.code);
      if (av == null) return 1;    // nulls always last
      if (bv == null) return -1;
      return mul * (av - bv);
    }
    return mul * compareCodes(a.code, b.code);
  });
  return arr;
}

// ── Cached fetch ─────────────────────────────────────────────────────────────

// One cached payload per period + impersonation target (the response is scoped to
// the effective user). Reopening the modal is instant; the data is an hourly blob,
// so it is refetched only after CACHE_MS. Failures are never cached.
const CACHE_MS = 10 * 60 * 1000;
const _cache = new Map();      // key -> { at, data }
const _inflight = new Map();   // key -> Promise

function cacheKey(period) { return `${period}|${getImpersonationEmail() || ''}`; }

function loadReport(period) {
  const key = cacheKey(period);
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_MS) return Promise.resolve(hit.data);
  if (_inflight.has(key)) return _inflight.get(key);
  const p = (async () => {
    const res = await fetch(`/api/diClientBillingReport/${encodeURIComponent(period)}`, { credentials: 'include' });
    const text = await res.text();
    let body = null;
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }
    if (!res.ok) throw new Error((body && body.error) || text || `HTTP ${res.status}`);
    const data = {
      rows: Array.isArray(body && body.rows) ? body.rows : [],
      groupings: {
        billingGroups: (body && body.groupings && body.groupings.billingGroups) || {},
        relationshipGroups: (body && body.groupings && body.groupings.relationshipGroups) || {},
      },
    };
    _cache.set(key, { at: Date.now(), data });
    return data;
  })().finally(() => _inflight.delete(key));
  _inflight.set(key, p);
  return p;
}

// { data, error, loading } for the report. Starts fetching when `enabled` first
// turns true (the modal opening); a cached copy is returned synchronously.
export function useClientBillingReport(period, enabled) {
  const [state, setState] = useState(() => {
    const hit = enabled && _cache.get(cacheKey(period));
    return { data: hit ? hit.data : null, error: '' };
  });
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const hit = _cache.get(cacheKey(period));
    if (hit && Date.now() - hit.at < CACHE_MS) {
      setState({ data: hit.data, error: '' });
      return undefined;
    }
    setState({ data: null, error: '' });
    loadReport(period)
      .then(data => { if (live) setState({ data, error: '' }); })
      .catch(err => { if (live) setState({ data: null, error: err.message || 'Failed to load' }); });
    return () => { live = false; };
  }, [period, enabled]);
  return { ...state, loading: enabled && !state.data && !state.error };
}
